import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel as getTanstackExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { Checkbox } from '../core/checkbox';

export type UseDataTableProps<TData> = Omit<TableOptions<TData>, 'data' | 'getCoreRowModel'> & {
  data: TData[] | undefined;

  /** Enables row expansion support for this table instance. */
  enableRowExpansion?: boolean;

  /**
   * Whether the data is being loaded initially. `data` would typically be undefined or have
   * missing pieces when this is true.
   */
  isLoading?: boolean;

  /**
   * True when data is present, but it's now stale and is being refreshed with new data, such as
   * when loading a new, uncached page.
   */
  isLoadingWithStaleData?: boolean;

  /** Whether there was an error fetching the data. */
  hasError?: boolean;

  /**
   * Number of skeleton rows rendered while `isLoading`. Defaults to the pagination page size
   * (or 10). Set a small value for tables that typically hold few rows.
   */
  loadingRowCount?: number;
};

const EMPTY_DATA: never[] = [];

export function useDataTable<TData extends Record<string, any>>({
  data,
  isLoading,
  isLoadingWithStaleData,
  hasError,
  loadingRowCount,
  enableRowExpansion = false,
  enableRowSelection = false,
  columns,
  manualPagination = true,
  manualSorting = true,
  getRowCanExpand,
  ...props
}: UseDataTableProps<TData>) {
  const computedColumns = useMemo<ColumnDef<TData>[]>(
    () =>
      enableRowSelection
        ? [
            {
              id: 'select',
              header: ({ table }) => (
                <Checkbox
                  checked={
                    table.getIsAllPageRowsSelected() ||
                    (table.getIsSomePageRowsSelected() && 'indeterminate')
                  }
                  onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                  disabled={
                    table.options.meta?.isLoading || table.options.meta?.isLoadingWithStaleData
                  }
                  aria-label="Select all"
                  className="mr-4 translate-y-0.5"
                />
              ),
              cell: ({ row }) => (
                <Checkbox
                  checked={row.getIsSelected()}
                  onCheckedChange={(value) => row.toggleSelected(!!value)}
                  aria-label="Select row"
                  className="mr-4 translate-y-0.5"
                />
              ),
              enableSorting: false,
              enableHiding: false,
              meta: {
                enableRowClick: false,
                loadingCell: <Checkbox disabled className="mr-4 translate-y-0.5" />,
              },
            },
            ...columns,
          ]
        : columns,
    [columns, enableRowSelection],
  );

  return useReactTable({
    meta: { isLoading, isLoadingWithStaleData, hasError, loadingRowCount },
    data: data ?? (EMPTY_DATA as TData[]),
    columns: computedColumns,
    getRowId: (row, index) => (typeof row.id === 'string' ? row.id : index.toString()),
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection,
    manualFiltering: true,
    ...(manualPagination
      ? { manualPagination }
      : {
          manualPagination: false,
          getPaginationRowModel: getPaginationRowModel(),
        }),
    ...(manualSorting
      ? { manualSorting }
      : {
          manualSorting: false,
          getSortedRowModel: getSortedRowModel(),
        }),
    ...(enableRowExpansion && {
      getExpandedRowModel: getTanstackExpandedRowModel(),
      getRowCanExpand: getRowCanExpand ?? (() => true),
    }),
    ...props,
  });
}
